import { useTranslation } from 'react-i18next';
import type { ErrorCode } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { formatUpdated } from '../lib/format.js';
import styles from './States.module.css';

/**
 * Состояния экрана: загрузка, пусто, ошибка, отозванный доступ.
 *
 * Макет задаёт вид карточек ошибки и пустого списка; скелетон строки таблицы и спиннер
 * собраны по тем же токенам, отдельного макета для них нет.
 */

export function TableSkeleton({ rows = 8 }: { rows?: number }): React.ReactElement {
  const { t } = useTranslation();
  const widths = ['70%', '92%', '56%', '84%', '64%', '78%', '88%', '60%'];

  return (
    <div className={styles.skeletonWrap} role="status" aria-live="polite">
      {Array.from({ length: rows }, (_, index) => (
        <div className={styles.skeletonRow} key={index}>
          <div className={styles.shimmer} style={{ width: 56 }} />
          <div className={styles.shimmer} style={{ maxWidth: widths[index % widths.length], flex: 1 }} />
          <div className={styles.shimmer} style={{ width: 88 }} />
          <div className={styles.shimmer} style={{ width: 64 }} />
        </div>
      ))}
      <div className={styles.skeletonFooter}>
        <span className={styles.spinner} aria-hidden="true" />
        {t('states.loading')}
      </div>
    </div>
  );
}

export function EmptyState({
  filtered,
  onResetFilters,
  onShowAll,
}: {
  filtered: boolean;
  onResetFilters: () => void;
  onShowAll: () => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.empty}>
      <div className={styles.emptyTitle}>{t('states.emptyTitle')}</div>
      <p className={styles.emptyBody}>
        {filtered ? t('states.emptyFiltered') : t('states.emptyPlain')}
      </p>
      {filtered && (
        <div className={styles.emptyActions}>
          <button type="button" className={styles.secondaryButton} onClick={onResetFilters}>
            {t('filters.resetAll')}
          </button>
          <button type="button" className={styles.primaryButton} onClick={onShowAll}>
            {t('states.allSections')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Карточка сбоя Jira: данные из кэша остаются на экране, рядом — причина и повтор. */
export function ErrorCard({
  error,
  cachedAt,
  onRetry,
}: {
  error: ApiRequestError;
  cachedAt?: string | null;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const at = cachedAt ? t('states.errorCachedAt', { time: formatUpdated(cachedAt, t) }) : '';

  return (
    <div className={styles.errorCard} role="alert">
      <div className={styles.errorHead}>
        <span className={styles.errorGlyph} aria-hidden="true">
          !
        </span>
        <span className={styles.errorTitle}>{t(`errors.${error.code}` satisfies `errors.${ErrorCode}`)}</span>
      </div>
      <p className={styles.errorBody}>{t('states.errorBody', { at })}</p>
      <div className={styles.errorActions}>
        <button type="button" className={styles.dangerButton} onClick={onRetry}>
          {t('states.retry')}
        </button>
        <a
          className={styles.errorLink}
          href="https://status.atlassian.com/"
          target="_blank"
          rel="noreferrer noopener"
        >
          {t('states.atlassianStatus')}
        </a>
      </div>
      {error.requestId && (
        <span className={`mono ${styles.requestId}`}>
          HTTP {error.status} · req {error.requestId.slice(0, 8)}
        </span>
      )}
    </div>
  );
}

/** Доступ отозван: планы остаются, нужно переподключить Jira. */
export function RevokedState({ onReconnect }: { onReconnect: () => void }): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.empty}>
      <div className={styles.emptyTitle}>{t('states.revokedTitle')}</div>
      <p className={styles.emptyBody}>{t('states.revokedBody')}</p>
      <button type="button" className={styles.primaryButton} onClick={onReconnect}>
        {t('states.reconnect')}
      </button>
    </div>
  );
}
